import { ArrowDownIcon, ArrowUpIcon, SortIcon } from "@/components/icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createColumnHelper,
  createSortedRowModel,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import { cn } from "cn";
import { useMemo, useState, type ReactNode } from "react";

/**
 * Solo la ordenación.
 *
 * En la versión 9 las funcionalidades se registran una a una en vez de venir
 * todas: lo que no se declara aquí ni existe ni pesa. Filtrar y paginar se
 * añadirán el día que una tabla los necesite de verdad.
 */
const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
});

export interface GridColumn<TRow extends RowData> {
  readonly key: string;
  readonly label: string;
  /** Los números se alinean a la derecha y ordenan como números. */
  readonly numeric?: boolean;
  readonly render: (row: TRow) => ReactNode;
  /** El valor por el que se ordena, si no es lo que se dibuja. */
  readonly sortValue?: (row: TRow) => string | number | boolean | null;
  /**
   * Para una columna sin orden ni texto --la casilla de «seleccionar la
   * página»--: lo que se ve en el encabezado EN VEZ del botón de orden con
   * `label`. La columna sigue contando para que cada encabezado quede sobre
   * su columna.
   */
  readonly renderHeader?: () => ReactNode;
}

interface Props<TRow extends RowData> {
  readonly columns: readonly GridColumn<TRow>[];
  readonly rows: readonly TRow[];
  readonly empty?: ReactNode;
  /** Con esto, cada fila es pulsable. */
  readonly onRowClick?: (row: TRow) => void;
  readonly className?: string;
}

/**
 * La tabla de toda la aplicación.
 *
 * Había tres hechas a mano --el resultado de una herramienta, el padrón y el
 * historial de versiones-- y ninguna ordenaba. Esta las sustituye: TanStack
 * Table pone el modelo de filas y la ordenación, y los primitivos de shadcn
 * ponen el marcado.
 *
 * Las columnas se declaran con una función de dibujo en vez de con un accesor
 * mágico: lo que se ve en la celda se lee en el mismo sitio donde se declara
 * la columna, sin ir a buscar un formateador por su nombre.
 */
export function DataGrid<TRow extends RowData>({
  columns,
  rows,
  empty = "No hay nada que enseñar.",
  onRowClick,
  className,
}: Props<TRow>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columnDefs = useMemo(() => {
    const helper = createColumnHelper<typeof features, TRow>();
    return helper.columns(
      columns.map((column) =>
        helper.accessor(
          (row: TRow) =>
            column.sortValue?.(row) ??
            ((row as Record<string, unknown>)[column.key] as
              string | number | boolean | null),
          {
            id: column.key,
            header: column.label,
            cell: (context) => column.render(context.row.original),
            meta: { numeric: column.numeric === true },
          },
        ),
      ),
    );
  }, [columns]);

  // `data` tiene que ser estable entre renders o la tabla se reconstruye
  // entera en cada uno.
  const data = useMemo(() => [...rows], [rows]);

  const table = useTable({
    features,
    columns: columnDefs,
    data,
    state: { sorting },
    onSortingChange: setSorting,
  });

  if (rows.length === 0) {
    /* Una cadena se dibuja como el párrafo apagado de siempre; cualquier
       otra cosa --un `EmptyState` con su icono-- se dibuja tal cual. */
    return typeof empty === "string" ? (
      <p className="text-sm text-muted-foreground">{empty}</p>
    ) : (
      empty
    );
  }

  const isNumeric = (meta: unknown): boolean =>
    (meta as { numeric?: boolean } | undefined)?.numeric === true;

  return (
    /* Scroll propio: una tabla ancha no puede estirar la página entera. */
    <div className={cn("w-full overflow-x-auto", className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const numeric = isNumeric(header.column.columnDef.meta);
                const sorted = header.column.getIsSorted();
                const renderHeader = columns.find(
                  (column) => column.key === header.column.id,
                )?.renderHeader;

                return (
                  <TableHead
                    key={header.id}
                    className={cn(numeric && "text-right")}
                  >
                    {renderHeader !== undefined ? (
                      renderHeader()
                    ) : (
                      <button
                        type="button"
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground",
                          numeric && "flex-row-reverse",
                        )}
                        onClick={() => {
                          header.column.toggleSorting();
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <table.FlexRender header={header} />
                        )}
                        {sorted === "asc" ? (
                          <ArrowUpIcon className="size-3" />
                        ) : sorted === "desc" ? (
                          <ArrowDownIcon className="size-3" />
                        ) : (
                          <SortIcon className="size-3 opacity-40" />
                        )}
                      </button>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={cn(onRowClick !== undefined && "cursor-pointer")}
              {...(onRowClick !== undefined
                ? {
                    onClick: () => {
                      onRowClick(row.original);
                    },
                  }
                : {})}
            >
              {row.getAllCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cn(
                    isNumeric(cell.column.columnDef.meta) &&
                      "text-right tabular-nums",
                  )}
                >
                  <table.FlexRender cell={cell} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
